import { Map } from 'ol';
import { Layer as OLayer } from 'ol/layer';
import type { Geometry } from 'ol/geom';
import VectorTile from 'ol/layer/VectorTile';
import LayerPMTiles from '../../../models/layers/layerpmtiles';
import { PMTilesVectorSource } from 'ol-pmtiles';
import StateManager from '../../../tools/state/statemanager';
// @ts-ignore
import * as SLDReader from '@nieuwlandgeo/sldreader';
import { Feature } from 'ol';
import { toFeature } from 'ol/render/Feature';

class PMTilesManager {
  map: Map;
  basemapLayers: VectorTile[] = [];
  activeLayers: Record<
    string,
    {
      olayer: OLayer;
      layer: LayerPMTiles;
    }
  > = {};

  constructor(map: Map) {
    this.map = map;
  }

  removeAllBasemapLayers() {
    this.basemapLayers.forEach((basemap) => {
      this.map.removeLayer(basemap);
    });
    this.basemapLayers = [];
  }

  addBasemapLayer(layer: LayerPMTiles) {
    const source = new PMTilesVectorSource({
      url: layer.source
    });
    const olayer = new VectorTile({ source: source });
    this.basemapLayers.push(olayer);

    // For basemap, set a minimal number (arbitrary defined to less than -5000)
    olayer.setZIndex(-5000 - layer.order);
    this.map.addLayer(olayer);
  }

  addLayer(layer: LayerPMTiles) {
    const source = new PMTilesVectorSource({
      url: layer.source
    });
    const olayer = new VectorTile({
      source: source,
      minResolution: layer.minResolution,
      maxResolution: layer.maxResolution
    });
    this.map.addLayer(olayer);
    this.activeLayers[layer.treeItemId] = { layer: layer, olayer: olayer };

    // see if there's an SLD file next to the pmtiles
    if( layer.style ){
      const sldUrl = layer.source.replace('.pmtiles', `-${layer.style}.sld`);
      fetch(sldUrl)
        .then((res) => res.text())
        .then((sldBody) => {
          const sldObject = SLDReader.Reader(sldBody);
          const sldLayer = SLDReader.getLayer(sldObject);
          const sldStyle = SLDReader.getStyle(sldLayer);
          const sldFeatureTypeStyle = sldStyle.featuretypestyles[0];
          const style = SLDReader.createOlStyleFunction(sldFeatureTypeStyle, {
            getProperty: (feat: Feature, propName: string) => {
              return feat.get(propName.toLowerCase());
            }
          });
          olayer.setStyle(style);
        });
    }
  }

  getLayer(layerFile: LayerPMTiles) {
    if (this.layerExists(layerFile)) {
      return this.activeLayers[layerFile.treeItemId].olayer;
    }
    return null;
  }

  removeLayer(layer: LayerPMTiles) {
    const olayer = this.activeLayers[layer.treeItemId].olayer;
    delete this.activeLayers[layer.treeItemId];
    this.map.removeLayer(olayer);
  }

  layerExists(layer: LayerPMTiles) {
    return layer.treeItemId in this.activeLayers;
  }

  public refreshZIndexes() {
    for (const obj of Object.values(this.activeLayers)) {
      const zindex = -obj.layer.order;
      obj.olayer.setZIndex(zindex);
    }
  }

  selectFeatures(extent: number[]) {
    // TODO: this is not really nice, as it shows only the part of the feature in the tile instead of the whole feature...
    for (const activeLayer of Object.values(this.activeLayers)) {
      const features: Feature[] = [];
      const olayer = activeLayer.olayer as VectorTile;
      for (const renderFeat of olayer.getFeaturesInExtent(extent)) {
        const feature = toFeature(renderFeat);
        const geom = feature.getGeometry() as Geometry;
        if (geom.intersectsExtent(extent)) {
          features.push(feature);
        }
      }
      if (features.length > 0) {
        StateManager.getInstance().state.selection.selectedFeatures.push(...features);
        StateManager.getInstance().state.interface.selectionComponentVisible = true;
        return; // stopping at first layer
      }
    }
  }
}

export default PMTilesManager;
